/**
 * Welcome Workspace — a hardcoded read-only workspace shown to fully
 * unauthenticated users (no hash, no identity, no vault).
 *
 * The app shell works normally (TopBar, sidebar, tabs, profile icon).
 * Only the workspace backing is faked with demo content that showcases
 * SecureBlob features (Mermaid, KaTeX, GFM tables, code blocks).
 *
 * The welcome file is intended to be rendered in preview mode.
 */

import type {
    Workspace, FileSystem, FileStat, History, Sharing,
    SyncState, CommitSummary,
    PresenceManager,
} from './types'

// ── Demo Content ───────────────────────────────────────────────

const WELCOME_MD = `# Welcome to SecureBlob 🔐

Your **encrypted workspace** — zero-knowledge, end-to-end encrypted, and entirely yours.

> Tap the **👤 profile icon** in the top bar to create a profile or sign in.

---

## How It Works

\`\`\`mermaid
flowchart LR
    A["📝 You write"] --> B["🔒 Encrypt locally"]
    B --> C["☁️ Store encrypted blob"]
    C --> D["🔑 Only your keys decrypt"]
    D --> A
\`\`\`

Your data is encrypted **before** it leaves your device. The server only sees ciphertext — it can never read your content.

---

## Rich Content

SecureBlob renders **math**, **diagrams**, **code**, and more — all inside your encrypted workspace.

### Mathematics

Euler's identity: $e^{i\\pi} + 1 = 0$

The Gaussian integral:

$$\\int_{-\\infty}^{\\infty} e^{-x^2} \\, dx = \\sqrt{\\pi}$$

### Code

\`\`\`typescript
const workspace = await engine.openWorkspace({
  blobId: "my-project",
  capability: shareLink.capability,
});

const files = workspace.fs.list();
console.log(\`\${files.length} encrypted files ready\`);
\`\`\`

### Tables

| Feature | Status |
|---|---|
| End-to-end encryption | ✅ |
| Offline-first sync | ✅ |
| Markdown preview | ✅ |
| Mermaid diagrams | ✅ |
| KaTeX math | ✅ |
| AI agent | ✅ |
| Collaboration via share links | ✅ |

### Checklist

- [x] Zero-knowledge encryption
- [x] Works offline
- [x] Sync across devices
- [ ] Your first note — **create a profile to get started!**

---

*Tap the **👤 profile icon** above to begin.*
`

// ── In-Memory File System ──────────────────────────────────────

const WELCOME_PATH = '/welcome.md'
const WELCOME_STAT: FileStat = {
    path: WELCOME_PATH,
    kind: 'file',
    mime: 'text/markdown',
    size: WELCOME_MD.length,
    mtime: Date.now(),
    ctime: Date.now(),
    lastWriterKid: 'demo-system', // Needed to show WriterBadge for blame toggle
}

function createWelcomeFs(): FileSystem {
    const noop = () => { }
    async function* emptyDirStream(): AsyncIterable<FileStat> {
        yield* []
    }
    return {
        list: () => [WELCOME_STAT],
        listDirAsync: async (path: string) => path === '/' ? [WELCOME_STAT] : [],
        listDirStream: (path: string) => path === '/' ? (async function* () { yield WELCOME_STAT })() : emptyDirStream(),
        stat: (path: string) => path === WELCOME_PATH ? WELCOME_STAT : null,
        readText: async (path: string) => path === WELCOME_PATH ? WELCOME_MD : null,
        readBytes: async () => null,
        readBinary: async () => null,
        createByteStream: async function* () { },
        writeText: noop,
        writeBytes: noop,
        patchBytes: async () => { },
        appendBytes: async () => { },
        delete: () => false,
        rename: () => false,
        mkdir: noop,
        symlink: noop,
        setMeta: noop,
    }
}

// ── Stub History & Sharing ─────────────────────────────────────

// ── Demo History ──────────────────────────────────────────────

const NOW = Date.now()
const ONE_HOUR = 3600 * 1000
const TWO_DAYS = 48 * ONE_HOUR
const SEVEN_DAYS = 7 * 24 * ONE_HOUR

// Deterministic mock commits
const DEMO_COMMITS: CommitSummary[] = [
    {
        commitHash: 'demo-hash-3',
        parents: ['demo-hash-2'],
        time: NOW - ONE_HOUR,
        writerKid: 'demo-bob',
        writerName: 'Bob',
        writerEmoji: '👨‍💻',
        message: 'Add Mermaid diagrams',
        affectedFiles: [{ path: WELCOME_PATH, changeType: 'modified', additions: 12, deletions: 4 }]
    },
    {
        commitHash: 'demo-hash-2',
        parents: ['demo-hash-1'],
        time: NOW - TWO_DAYS,
        writerKid: 'demo-alice',
        writerName: 'Alice',
        writerEmoji: '👩‍🔬',
        message: 'Update security documentation',
        affectedFiles: [{ path: WELCOME_PATH, changeType: 'modified', additions: 5, deletions: 1 }]
    },
    {
        commitHash: 'demo-hash-1',
        parents: [],
        time: NOW - SEVEN_DAYS,
        writerKid: 'demo-system',
        writerName: 'System',
        writerEmoji: '🤖',
        message: 'Initial commit',
        affectedFiles: [{ path: WELCOME_PATH, changeType: 'added', additions: 89, deletions: 0 }]
    }
]

const WELCOME_HISTORY: History = {
    list: () => DEMO_COMMITS,
    listForFile: (path) => path === WELCOME_PATH ? DEMO_COMMITS : [],
    getCommit: async (hash) => {
        const commit = DEMO_COMMITS.find(c => c.commitHash === hash)
        if (!commit) throw new Error('Commit not found')
        return commit
    },
    blame: async (path) => {
        if (path !== WELCOME_PATH) return []
        // Mock blame distribution
        return WELCOME_MD.split('\n').map((line, i) => {
            // Distribute lines among demo commits
            // Lines 0-20: Initial (System)
            // Lines 21-40: Update (Alice)
            // Lines 41+: Diagrams (Bob)
            let commit = DEMO_COMMITS[2] // Initial
            if (i >= 40) commit = DEMO_COMMITS[0] // Bob
            else if (i >= 20) commit = DEMO_COMMITS[1] // Alice

            return {
                lineNumber: i + 1,
                text: line,
                commitHash: commit.commitHash,
                writerKid: commit.writerKid,
                writerName: commit.writerName,
                timestamp: commit.time,
            }
        })
    },
    listDirAtCommit: async (path) => {
        if (path !== '/') return []
        return [{
            path: WELCOME_PATH,
            kind: 'file',
            mime: 'text/markdown',
            size: WELCOME_MD.length,
            mtime: NOW,
            ctime: NOW - SEVEN_DAYS,
        }]
    },
    readTextAtCommit: async (path) => {
        // Simplify: just return current text for all demo commits to avoid complex diff logic in demo
        // (Real diffs would require storing old versions of WELCOME_MD)
        return path === WELCOME_PATH ? WELCOME_MD : null
    },
    readBytesAtCommit: async () => null,
    diffText: async () => [],
}

const EMPTY_SHARING: Sharing = {
    createShareLink: () => ({ url: '', mode: 'read' }),
    createInviteLink: async () => ({ url: '', mode: 'read' }),
}

// ── Stub Presence ──────────────────────────────────────────────

const EMPTY_PRESENCE: PresenceManager = {
    startHeartbeat: () => { },
    stopHeartbeat: () => { },
    getViewers: () => [],
    on: () => () => { },
}

const SYNCED_STATE: SyncState = {
    syncing: false,
    online: true,
    connectivity: 'connected',
    synced: true,
    lastSyncAt: Date.now(),
    pendingChanges: 0,
    syncLog: [],
    syncPolicy: 'on-demand',
}

// ── Welcome Workspace Factory ──────────────────────────────────

export const WELCOME_BLOB_ID = '__welcome__'

export function createWelcomeWorkspace(): Workspace {
    return {
        blobId: WELCOME_BLOB_ID,
        canWrite: false,
        label: 'SecureBlob',
        emoji: '🔐',

        state: SYNCED_STATE,
        fs: createWelcomeFs(),
        history: WELCOME_HISTORY,
        sharing: EMPTY_SHARING,
        presence: EMPTY_PRESENCE,

        on: () => () => { },
        off: () => { },
        syncNow: async () => { },
        getSyncPolicy: () => 'on-demand',
        setSyncPolicy: () => { },
        prefetchContent: async () => { },
        close: () => { },
        commitTombstone: async () => { },
        createGenesisCommit: async () => { },
        getWriters: () => [],
        removeWriter: async () => { },
        acquireResourceLease: async () => null,
        refreshResourceLease: async () => null,
        releaseResourceLease: async () => false,
        pauseSync: () => { },
        resumeSync: () => { },
        resolveWriterName: (kid: string) => {
            const commit = DEMO_COMMITS.find(c => c.writerKid === kid)
            return commit ? { name: commit.writerName, emoji: commit.writerEmoji } : {}
        },
    }
}
